import { useEffect, useState, useRef } from 'react';
import { Plus, Pencil, Trash2, User, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getManagers, createManager, updateManager, deleteManager, uploadFile } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankManagerDraft = {
  name: '',
  photo: '',
  role: 'HR менеджер',
  telegram_link: '',
  whatsapp_link: '',
  is_active: true,
  rotation_weight: 0,
  show_telegram: true,
  show_whatsapp: true
};

export default function AdminManagers() {
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removePromptOpen, setRemovePromptOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [draft, setDraft] = useState(blankManagerDraft);
  const [isUploading, setIsUploading] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getManagers();
      setManagers(res.data);
    } catch (err) {
      toast.error('Не удалось загрузить менеджеров');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = () => {
    setActiveRow(null);
    setDraft(blankManagerDraft);
    setEditorOpen(true);
  };

  const startEdit = (manager) => {
    setActiveRow(manager);
    setDraft({
      name: manager.name,
      photo: manager.photo,
      role: manager.role,
      telegram_link: manager.telegram_link,
      whatsapp_link: manager.whatsapp_link,
      is_active: manager.is_active,
      rotation_weight: manager.rotation_weight || 0,
      show_telegram: manager.show_telegram !== false,
      show_whatsapp: manager.show_whatsapp !== false
    });
    setEditorOpen(true);
  };

  const askDelete = (manager) => {
    setActiveRow(manager);
    setRemovePromptOpen(true);
  };

  const persistDraft = async () => {
    if (!draft.name) {
      toast.error('Укажите имя менеджера');
      return;
    }

    try {
      if (activeRow) {
        await updateManager(activeRow.id, draft);
        toast.success('Менеджер обновлён');
      } else {
        await createManager(draft);
        toast.success('Менеджер создан');
      }
      setEditorOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось сохранить');
    }
  };

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Файл слишком большой. Максимум 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const res = await uploadFile(file);
      // URL уже содержит /api/uploads/...
      const fullUrl = `${API_URL}${res.data.url}`;
      setDraft({ ...draft, photo: fullUrl });
      toast.success('Фото загружено');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Не удалось загрузить фото');
    } finally {
      setIsUploading(false);
    }
  };

  const performDelete = async () => {
    if (!activeRow) return;

    try {
      await deleteManager(activeRow.id);
      toast.success('Менеджер удалён');
      setRemovePromptOpen(false);
      fetchRows();
    } catch (err) {
      toast.error('Не удалось удалить');
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-managers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Менеджеры</h1>
          <p className="text-slate-500">Управление командой менеджеров</p>
        </div>
        <Button onClick={startCreate} data-testid="add-manager-button">
          <Plus className="w-4 h-4 mr-2" />
          Добавить
        </Button>
      </div>

      {managers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Нет менеджеров</p>
            <Button variant="outline" className="mt-4" onClick={startCreate}>
              Добавить первого менеджера
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {managers.map((manager) => (
            <Card key={manager.id} data-testid={`manager-card-${manager.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                    {manager.photo ? (
                      <img src={manager.photo} alt={manager.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{manager.name}</h3>
                      {manager.is_active ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Активен</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full">Неактивен</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{manager.role}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {manager.telegram_link && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">TG:</span>
                      <a href={manager.telegram_link} target="_blank" rel="noopener noreferrer" className="truncate hover:text-sky-600">
                        {manager.telegram_link}
                      </a>
                    </div>
                  )}
                  {manager.whatsapp_link && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">WA:</span>
                      <a href={manager.whatsapp_link} target="_blank" rel="noopener noreferrer" className="truncate hover:text-green-600">
                        {manager.whatsapp_link}
                      </a>
                    </div>
                  )}
                  {manager.rotation_weight > 0 ? (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">Вес:</span>
                      <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-mono font-medium">
                        {manager.rotation_weight}/10
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="text-slate-400">Вес:</span>
                      <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full font-medium">
                        выкл
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => startEdit(manager)}
                    data-testid={`edit-manager-${manager.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => askDelete(manager)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-manager-${manager.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeRow ? 'Редактировать менеджера' : 'Новый менеджер'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Имя *</Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Анна Петрова"
                data-testid="manager-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Фото</Label>
              <input
                type="file"
                ref={pickerRef}
                onChange={onPhotoPick}
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
              />
              
              {draft.photo ? (
                <div className="space-y-2">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-100 mx-auto">
                    <img src={draft.photo} alt="Фото" className="w-full h-full object-cover" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => pickerRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Заменить фото
                  </Button>
                </div>
              ) : (
                <div
                  onClick={() => !isUploading && pickerRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {isUploading ? (
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Нажмите для загрузки фото</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Роль</Label>
              <Input
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                placeholder="HR менеджер"
                data-testid="manager-role-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Telegram ссылка</Label>
              <Input
                value={draft.telegram_link}
                onChange={(e) => setDraft({ ...draft, telegram_link: e.target.value })}
                placeholder="https://t.me/username"
                data-testid="manager-telegram-input"
              />
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm text-slate-600">Показывать кнопку Telegram</span>
                <Switch
                  checked={draft.show_telegram}
                  onCheckedChange={(v) => setDraft({ ...draft, show_telegram: v })}
                  data-testid="manager-show-telegram-switch"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>WhatsApp ссылка</Label>
              <Input
                value={draft.whatsapp_link}
                onChange={(e) => setDraft({ ...draft, whatsapp_link: e.target.value })}
                placeholder="https://wa.me/1234567890"
                data-testid="manager-whatsapp-input"
              />
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm text-slate-600">Показывать кнопку WhatsApp</span>
                <Switch
                  checked={draft.show_whatsapp}
                  onCheckedChange={(v) => setDraft({ ...draft, show_whatsapp: v })}
                  data-testid="manager-show-whatsapp-switch"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Вес в ротации (0-10)</Label>
              <Input
                type="number"
                min="0"
                max="10"
                value={draft.rotation_weight}
                onChange={(e) => {
                  const v = parseInt(e.target.value);
                  const clamped = Number.isNaN(v) ? 0 : Math.max(0, Math.min(10, v));
                  setDraft({ ...draft, rotation_weight: clamped });
                }}
                data-testid="manager-weight-input"
              />
              <p className="text-xs text-slate-500">
                0 = не участвует во взвешенной ротации. 1–10 — доля трафика. Работает только в
                режиме «Случайный». Если хотя бы у одного менеджера вес &gt; 0 — распределение
                идёт по весам (автобалансировка временно выключается).
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label>Активен</Label>
              <Switch
                checked={draft.is_active}
                onCheckedChange={(checked) => setDraft({ ...draft, is_active: checked })}
                data-testid="manager-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Отмена
            </Button>
            <Button onClick={persistDraft} data-testid="save-manager-button">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={removePromptOpen} onOpenChange={setRemovePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить менеджера?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить {activeRow?.name}? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-manager"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
